import * as React from 'react'
import cn from 'clsx'
import { useKeenSlider } from 'keen-slider/react'

import { ProductGridItem } from '~/components/common/product-grid-item.tsx'
import type { GetProduct } from '~/models/ecommerce-providers/sylius/shop/fetch/product/get-product.ts'
import { Heading, Text } from '@radix-ui/themes'

type ProductsSliderProps = {
  products: GetProduct[]
  title?: string
  subtitle?: string
}

const ProductsSlider: React.FC<ProductsSliderProps> = ({ products, title, subtitle }) => {
  const animation = { duration: 100000, easing: (t: number) => t }
  const [isMounted, setIsMounted] = React.useState(false)

  const [ref, slider] = useKeenSlider<HTMLDivElement>({
    loop: true,
    renderMode: 'performance',
    mode: 'free',
    slides: { origin: 'center', perView: () => Math.min(window.innerWidth / 408, 7.5, products.length), spacing: 60 },
    created(s) {
      setIsMounted(true)
      s.moveToIdx(5, true, animation)
    },
    updated(s) {
      s.moveToIdx(s.track.details.abs + 5, true, animation)
    },
    animationEnded(s) {
      s.moveToIdx(s.track.details.abs + 5, true, animation)
    },
  })

  return products?.length ? (
    <div className="flex flex-col gap-y-2 mt-32">
      {!!title && (
        <Heading color="green" weight="medium" size="7" align="center">
          {title}
        </Heading>
      )}
      {!!subtitle && (
        <Text color="gray" className="text-center" align="center">
          {subtitle}
        </Text>
      )}
      <div
        ref={ref}
        className={cn('transition-opacity duration-300 rounded opacity-0 relative py-20', {
          'opacity-100': isMounted,
        }, 'keen-slider')}
        onMouseEnter={() => {
          if (slider.current) {
            slider.current.animator.stop()
          }
        }}
        onMouseLeave={() => {
          if (slider.current) {
            slider.current.moveToIdx(
              slider.current.track.details.abs + 5,
              true,
              animation
            )
          }
        }}
      >
        {// @ts-ignore
          products?.map((product: GetProduct) => (
            <ul key={product.id} className="list-none">
              <ProductGridItem
                key={product.id}
                product={product}
                className="keen-slider__slide !overflow-visible"
              />
            </ul>
          ))}
      </div>
    </div>
  ) : null
}
ProductsSlider.displayName = 'ProductsSlider'

export { ProductsSlider }
